import { useState } from 'react'

import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import { DatePicker } from '@mui/x-date-pickers'

import { addWeeks } from '@util/date-util'

import { StyledError } from '../../../../theme/components/StyledError'
import { useUpdateSubprocesForEntity } from '../../hooks/useUpdateSubprocesForEntity'
import type { Subproces } from '../../types/Subproces'

interface Props {
  subproces: Subproces
  onClose: () => void
}

export const ReviewSchouw = ({ subproces, onClose }: Props) => {
  const { mutate: updateSubprocesForEntity, error } =
    useUpdateSubprocesForEntity()

  const [errors, setErrors] = useState<
    Partial<Record<keyof Subproces | 'text', boolean>>
  >({})

  const [isApproved, setIsApproved] = useState(true)
  const [schouwDatum, setSchouwDatum] = useState<Date | null>(new Date())
  const [dueDate, setDueDate] = useState<Date | null>(addWeeks(new Date(), 1))

  const validate = () => {
    const dueDateError = !isApproved && !dueDate
    const schouwDatumError = !schouwDatum

    setErrors({
      schouwDatum: schouwDatumError,
      dueDate: dueDateError,
    })

    return !(schouwDatumError || dueDateError)
  }

  const onSubmitClick = () => {
    if (validate()) {
      const proces: Partial<Subproces & { text: string }> = {
        _id: subproces._id,
        status: isApproved ? 'done' : 'rejected',
        schouwDatum: schouwDatum as unknown as string,
      }

      if (!isApproved) {
        proces.dueDate = dueDate as unknown as string
      }

      const body = {
        entity: subproces.entity,
        subprocesInfo: proces,
      }

      updateSubprocesForEntity(body, { onSuccess: () => onClose() })
    }
  }

  return (
    <>
      <DialogContent>
        <StyledError error={error} />
        <FormControl>
          <FormControlLabel
            value={isApproved}
            control={<Switch checked={isApproved} />}
            label={'Schouw is goedgekeurd'}
            onChange={() => {
              setIsApproved(!isApproved)
            }}
          />
        </FormControl>

        <Grid container>
          <Grid size={{ xs: 6 }}>
            <DatePicker
              label="Schouw uitgevoerd op"
              value={schouwDatum}
              onChange={setSchouwDatum}
              displayWeekNumber
              disableFuture
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'standard',
                  sx: { pr: 1, my: 2 },
                  error: errors.schouwDatum,
                  fullWidth: true,
                },
              }}
            />
          </Grid>
          {!isApproved && (
            <Grid size={{ xs: 6 }}>
              <DatePicker
                label="Nieuwe datum waarvoor schouw ingepland moet worden"
                value={dueDate}
                onChange={(e) => {
                  setErrors({ ...errors, dueDate: false })
                  setDueDate(e)
                }}
                displayWeekNumber
                disabled={true}
                disablePast
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    variant: 'standard',
                    sx: { pr: 1, my: 2 },
                    error: errors.dueDate,
                    fullWidth: true,
                    helperText: errors.dueDate ? 'Verplicht' : undefined,
                  },
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose} autoFocus>
          Annuleren
        </Button>
        <Button onClick={onSubmitClick} variant={'contained'}>
          {isApproved ? 'Goedkeuren' : 'Afkeuren'}
        </Button>
      </DialogActions>
    </>
  )
}
